import React, { useState } from 'react';
import { Text } from '@components/Text';
import { RedesignButton } from '@components/RedesignButton';
import { InputTextarea } from 'primereact/inputtextarea';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { useAppDispatch } from 'src/hooks';
import { toastNotificationsSlice } from 'src/redesign/features/Toast/store/reducer';
import moment from 'moment-timezone';
import S from './RequestModal.module.scss';

interface CancelRefundRequestModalProps {
  request: any;
  onApprove?: (requestId: string, comment: string) => void;
  onDeny?: (requestId: string, comment: string) => void;
}

export const CancelRefundRequestModal: React.FC<CancelRefundRequestModalProps> = ({
  request,
  onApprove,
  onDeny,
}) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeCurrentModalAction());
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      if (onApprove) {
        await onApprove(request.id, comment);
      }

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: 'Cancellation request approved successfully.',
          life: 5000,
        }),
      );

      handleClose();
    } catch (error) {
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to approve request. Please try again.',
          life: 5000,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setIsSubmitting(true);
    try {
      if (onDeny) {
        await onDeny(request.id, comment);
      }

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: 'Cancellation request declined.',
          life: 5000,
        }),
      );

      handleClose();
    } catch (error) {
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to decline request. Please try again.',
          life: 5000,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format event date/time with timezone
  const formatEventDateTime = (dateString: string, timezone?: string) => {
    const eventMoment = timezone ? moment.tz(dateString, timezone) : moment(dateString);
    return eventMoment.format('dddd, MMMM Do YYYY [at] h:mm A z');
  };

  return (
    <div className={S.modalContent}>
      <div className={S.reasonSection}>
        <Text variation="body-small" bold className={S.sectionLabel}>
          Reason:
        </Text>
        <Text variation="body" className={S.reason}>
          {request.reason}
        </Text>
      </div>

      <div className={S.detailsSection}>
        <div className={S.detailRow}>
          <Text variation="body-small" bold className={S.detailLabel}>
            Total Refund Amount:
          </Text>
          <Text variation="h5" className={S.refundAmount}>
            ${request.refund_amount?.toFixed(2) || '0.00'}
          </Text>
        </div>

        <div className={S.detailRow}>
          <div className={S.eventDetailsContainer}>
            <div className={S.eventDetailColumn}>
              <Text variation="body" bold className={S.detailLabel}>
                Event Name:
              </Text>
              <Text variation="body-small">{request.event.title}</Text>
            </div>
            <div className={S.eventDetailColumn}>
              <Text variation="body" bold className={S.detailLabel}>
                Event Date & Time:
              </Text>
              <Text variation="body-small">
                {formatEventDateTime(request.event.start_date, request.event.timezone)}
              </Text>
            </div>
          </div>
        </div>

        <div className={S.detailRow}>
          <Text variation="body" bold className={S.detailLabel}>
            Attendees:
          </Text>
          <div className={S.attendeesList}>
            {request.attendees.map((attendee: any, index: number) => (
              <Text key={attendee.id} variation="body-small">
                {attendee.first_name} {attendee.last_name} ({attendee.email})
                {index < request.attendees.length - 1 && ', '}
              </Text>
            ))}
          </div>
        </div>

        <div className={S.detailRow}>
          <Text variation="body" bold className={S.detailLabel}>
            Order ID:
          </Text>
          <Text variation="body-small">#{request.cart.friendly_cart_id}</Text>
        </div>
      </div>

      <div className={S.commentSection}>
        <Text variation="body" bold className={S.sectionLabel}>
          Admin Comment (Optional):
        </Text>
        <InputTextarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment for the attendee..."
          className={S.commentTextarea}
          rows={3}
        />
      </div>

      <div className={S.modalActions}>
        <RedesignButton
          variation="secondary"
          height={52}
          onClick={handleDeny}
          disabled={isSubmitting}
          className={S.denyButton}
        >
          <Text regular color="error-400">
            {isSubmitting ? 'Processing...' : 'Decline'}
          </Text>
        </RedesignButton>

        <RedesignButton
          variation="primary"
          height={52}
          onClick={handleApprove}
          disabled={isSubmitting}
          className={S.approveButton}
        >
          <Text regular color="white-100">
            {isSubmitting ? 'Processing...' : 'Accept'}
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
